import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';

import { auth } from '../firebase';
import { ensureUserDocuments } from '../lib/account';
import { passwordSignInMessage, RESET_REQUESTED_MESSAGE } from '../lib/authLink';
import { errorCode, errorMessage } from '../lib/errors';
import AuthLayout, { Divider } from './AuthLayout';
import Field from './Field';
import GoogleButton from './GoogleButton';
import TopPuns from './TopPuns';

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);

      // Also backfills accounts old enough to predate these documents.
      await ensureUserDocuments(user);

      navigate('/today');
    } catch (err) {
      console.error('[Login] sign in failed:', err);
      // Covers the Google-only account too: there is no way to tell "wrong
      // password" from "this account never had one" without leaking whether the
      // address is registered, so one message answers both. See authLink.ts.
      setError(passwordSignInMessage(errorCode(err)) || errorMessage(err) || 'Could not sign in. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!email.trim()) {
      setError('Type your email address first.');
      return;
    }

    setResetting(true);
    setError(null);

    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      console.error('[Login] password reset failed:', err);

      /*
       * `user-not-found` is reported as success on purpose.
       *
       * It is returned both for an address with no account and for a Google
       * account, which has no password to reset. Saying so out loud was an
       * enumeration oracle — anyone could test addresses and learn who plays —
       * and it was wrong for the Google case, where an account does exist.
       * The neutral message covers both and tells a real user what to do next.
       */
      if (errorCode(err) === 'auth/user-not-found') {
        setResetSent(true);
      } else {
        setError('Could not send the reset email. Try again.');
      }
    } finally {
      setResetting(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back."
      subtitle="Back for more punishment. One prompt a day, same as ever."
      aside={<TopPuns />}
    >
      {error && (
        <p className="mb-4 rounded-xl border-2 border-ink bg-coral px-4 py-3 text-sm font-medium text-white shadow-pop-sm">
          {error}
        </p>
      )}

      {resetSent && (
        <p className="mb-4 rounded-xl border-2 border-ink bg-lagoon px-4 py-3 text-sm font-medium text-white shadow-pop-sm">
          {RESET_REQUESTED_MESSAGE}
        </p>
      )}

      <GoogleButton onDone={() => navigate('/today')} onError={(message) => setError(message || null)} />

      <Divider />

      <form onSubmit={handleLogin} className="space-y-4">
        <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />

        <button
          type="submit"
          disabled={loading}
          className="press w-full rounded-xl border-2 border-ink bg-mustard px-5 py-3 font-display text-lg font-bold shadow-pop disabled:cursor-not-allowed disabled:bg-ink/10 disabled:text-ink/30"
        >
          {loading ? 'Signing in...' : 'Sign in'}
        </button>
      </form>

      <div className="mt-5 space-y-3 text-sm">
        <button
          onClick={handleReset}
          disabled={resetting}
          className="text-ink/60 underline underline-offset-2 hover:text-ink"
        >
          {resetting ? 'Sending...' : 'Forgot your password?'}
        </button>

        <p className="text-ink/60">
          New here?{' '}
          <Link to="/signup" className="font-semibold text-ink underline underline-offset-2">
            Make an account
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}

export default Login;
